import React, { useState, useEffect, useContext, useCallback } from 'react';
import { fmtDateTime as _fmtDateTime } from '@/lib/dates';
import axios from 'axios';
import { AuthContext } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  ShieldAlert, FileText, DollarSign, Lock, RotateCcw, Send, CheckCircle2, Plus, Trash2, History, Download, Eye,
} from 'lucide-react';
import { VerificationCapture } from '@/components/wom/VerificationCapture';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ID_LABELS = [
  ['rout_service_request_number', 'ROUT Service Request #'],
  ['rout_work_order_number', 'ROUT Work Order #'],
  ['client_reference_number', 'Client Reference/Ticket #'],
  ['client_work_order_number', 'Client Work Order #'],
  ['client_po_number', 'Client PO #'],
];

export const BillingPanel = ({ workOrderId, onFixCloseout }) => {
  const { token, user } = useContext(AuthContext);
  const headers = { headers: { Authorization: `Bearer ${token}` } };
  const isGA = user?.role === 'owner';
  const canVerify = ['owner', 'admin', 'manager', 'tfs_operations'].includes(user?.role);
  const [rev, setRev] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  // Service-fee selection (Proposed Client Billing)
  const [feeType, setFeeType] = useState('none');
  const [feeTemplateId, setFeeTemplateId] = useState('');
  const [oneTimeAmount, setOneTimeAmount] = useState('');
  const [oneTimeName, setOneTimeName] = useState('');
  const [manageFees, setManageFees] = useState(false);
  const [feeDraft, setFeeDraft] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/billing/work-orders/${workOrderId}/review`, headers);
      setRev(r.data);
      // The service-fee line is managed by the Service Fee selector — keep it out of the editable list.
      setItems((r.data.billing_line_items || []).filter((li) => li.source !== 'service_fee'));
      const sf = r.data.service_fee || {};
      setFeeType(sf.type || (Number(sf.amount) > 0 ? '' : 'none'));
      setFeeTemplateId(sf.template_id || '');
      if (sf.type === 'one_time') { setOneTimeAmount(sf.amount != null ? String(sf.amount) : ''); setOneTimeName(sf.name || ''); }
      else if (!sf.type && Number(sf.amount) > 0) { setOneTimeAmount(String(sf.amount)); }
    } catch (e) { /* not ops */ }
  }, [workOrderId, token]);

  useEffect(() => { load(); }, [load]);
  if (!rev) return null;

  const feeTemplates = rev.customer?.service_fee_templates || [];
  const saveServiceFee = async () => {
    let body = { service_fee_type: feeType };
    if (feeType === 'pre_established') {
      const t = feeTemplates.find((x) => x.id === feeTemplateId);
      if (!t) { toast.error('Select a pre-established fee'); return; }
      body = { ...body, service_fee_template_id: t.id };
    } else if (feeType === 'one_time') {
      if (!(Number(oneTimeAmount) > 0) || !oneTimeName.trim()) { toast.error('Enter a fee description and an amount greater than 0'); return; }
      body = { ...body, service_fee_name: oneTimeName.trim(), service_call_amount: Number(oneTimeAmount) };
    } else if (feeType === 'none') {
      if (Number(rev.service_fee?.amount) > 0 &&
          !window.confirm('Remove the existing service-fee charge from this work order?')) return;
    } else {
      toast.error('Choose a service-fee option'); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/billing/work-orders/${workOrderId}/service-fee`, body, headers);
      toast.success('Service fee saved to one billing line'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };
  const openManageFees = () => { setFeeDraft(feeTemplates.map((t) => ({ ...t }))); setManageFees(true); };
  const saveFeeTemplates = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/customers/${rev.customer?.id}/service-fees`, { service_fee_templates: feeDraft }, headers);
      toast.success('Service fee templates saved'); setManageFees(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };

  const openPdf = async (name, filename, download) => {
    try {
      const r = await axios.get(`${API}/billing/work-orders/${workOrderId}/${name}`, { ...headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (download) {
        const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error('Could not open PDF'); }
  };

  const act = async (path, body, ok) => {
    setBusy(true);
    try { await axios.post(`${API}/billing/work-orders/${workOrderId}/${path}`, body || {}, headers); toast.success(ok); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };
  const saveItems = async () => {
    setBusy(true);
    try { await axios.put(`${API}/billing/work-orders/${workOrderId}/line-items`, { line_items: items }, headers); toast.success('Billing line items saved'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };
  const doReopen = () => { const reason = window.prompt('Reason for reopening this work order?'); if (reason && reason.trim()) act('reopen', { reason }, 'Work order reopened'); };
  const doReturn = () => { const reason = window.prompt('Reason for returning (incomplete closeout)?'); if (reason && reason.trim()) act('return', { reason }, 'Returned to operations'); };
  const loadPreview = async () => {
    try { const r = await axios.get(`${API}/billing/work-orders/${workOrderId}/package-preview`, headers); setPreview(r.data); setShowPreview(true); }
    catch (e) { toast.error('Preview unavailable'); }
  };

  const st = rev.billing_status;
  const ready = rev.billing_readiness ? rev.billing_readiness.ready : true;
  const woStatus = rev.status;
  const inProgressPhase = ['Open - Unassigned', 'Assigned', 'Scheduled', 'Dispatched', 'En Route', 'In Progress', 'On Hold', 'Awaiting Authorization'].includes(woStatus);
  const closeoutPhase = woStatus === 'Awaiting Administrative Closeout';
  const billingPhase = !inProgressPhase && !closeoutPhase;
  const serviceFee = Number(rev.service_fee?.amount || 0);
  const lineTotal = items.reduce((s, li) => s + (Number(li.amount) || (Number(li.quantity || 0) * Number(li.unit_price || 0))), 0);
  const total = Math.round((serviceFee + lineTotal) * 100) / 100;
  const internalCost = rev.internal_cost_total || 0;
  const gp = total - internalCost;
  const marginCalculable = total > 0 && internalCost > 0;
  const margin = marginCalculable ? ((gp / total) * 100).toFixed(1) : null;
  const icb = rev.internal_cost_breakdown || {};

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="billing-panel">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[#0E2A47]"><DollarSign size={18} /><h3 className="text-base font-bold">Review &amp; Billing</h3></div>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600" data-testid="billing-status">{st}</span>
      </div>

      {rev.data_exception && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="billing-data-exception">
          <ShieldAlert size={18} className="mt-0.5" /><div><b>{rev.data_exception}</b> — billing is blocked until corrected.</div>
        </div>
      )}
      {rev.operational_locked && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="billing-locked-note"><Lock size={13} /> Completed — operational controls locked. Only Status/Reopen available.</div>
      )}

      {/* Five separate identifiers */}
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="billing-identifiers">
        {ID_LABELS.map(([k, label]) => (
          <div key={k} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
            <div className="font-mono text-sm text-[#0E2A47]">{rev.identifiers[k] || '—'}</div>
          </div>
        ))}
      </div>

      {rev.missing_required_numbers?.length > 0 && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-700">This customer requires: {rev.missing_required_numbers.join(', ')} before billing submission.</div>
      )}

      {/* Resolved service details (authoritative — from master records / completion payload) */}
      {rev.service_context && (
        <div className="mb-4 rounded-lg border border-slate-200 p-3" data-testid="billing-service-context">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Service Details</div>
          <div className="grid gap-x-4 gap-y-1.5 text-xs sm:grid-cols-2">
            {[
              ['Job / Service', rev.service_context.job_title],
              ['Full Address', rev.service_context.full_address],
              ['Date Received', rev.service_context.date_received],
              ['Service Date', rev.service_context.service_date],
              ...(rev.service_context.scheduled_date ? [['Scheduled Date', rev.service_context.scheduled_date]] : []),
              ['Fulfillment Type', rev.service_context.fulfillment_type],
              ['Completion Status', rev.service_context.completion_status],
              ['Completion Date', rev.service_context.completion_date],
              ['Verification', rev.service_context.verification],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between gap-2 border-b border-slate-50 py-0.5">
                <span className="text-slate-400">{label}</span>
                <span className="text-right font-medium text-slate-700">{value || '—'}</span>
              </div>
            ))}
          </div>
          {rev.service_context.follow_up_required && (
            <div className="mt-2 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800" data-testid="billing-followup-note">
              {rev.service_context.follow_up_statement}
              {rev.service_context.follow_up_date && ` · Reminder scheduled for ${rev.service_context.follow_up_date}.`}
            </div>
          )}
        </div>
      )}

      {inProgressPhase && (
        <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="billing-inprogress-note">
          Billing review begins after the work is completed. Billing information is naturally incomplete while service is still being performed.
        </div>
      )}
      {closeoutPhase && rev.billing_readiness && (
        <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-3 text-xs text-blue-700" data-testid="billing-closeout-checklist">
          <div className="mb-1 font-semibold">Field completion checklist</div>
          {rev.billing_readiness.missing.length === 0
            ? <span>All field-completion items recorded — ready to submit for billing review.</span>
            : <ul className="ml-1 list-disc pl-4 space-y-0.5">{rev.billing_readiness.missing.map((m) => <li key={m}>{m}</li>)}</ul>}
        </div>
      )}
      {billingPhase && rev.billing_readiness && !rev.billing_readiness.ready && (
        <div className="mb-3 rounded-lg border border-red-300 bg-red-50 px-3 py-3 text-xs text-red-700" data-testid="billing-readiness-warning">
          <div className="mb-1 flex items-center gap-1.5 font-semibold"><ShieldAlert size={14} /> Closeout Requirements Incomplete</div>
          <ul className="ml-1 list-disc space-y-0.5 pl-4">
            {rev.billing_readiness.missing.map((m) => (
              <li key={m} data-testid={`readiness-missing-${m.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>{m}</li>
            ))}
          </ul>
          <div className="mt-1.5 text-[11px] text-red-500">Resolve these in the closeout below. Submit and Approve are disabled until complete.</div>
          {onFixCloseout && (
            <Button size="sm" variant="outline" className="mt-2 border-red-300 text-red-700 hover:bg-red-100" onClick={onFixCloseout} data-testid="billing-fix-closeout-btn">
              Complete or Correct Closeout
            </Button>
          )}
        </div>
      )}
      {billingPhase && rev.billing_readiness && rev.billing_readiness.ready && (
        <div className="mb-3 flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700" data-testid="billing-readiness-ok">
          <CheckCircle2 size={14} /> Ready for billing review.
        </div>
      )}

      {/* Customer / OM verification capture (shared record) */}
      <VerificationCapture workOrderId={workOrderId} verification={rev.verification} canEdit={canVerify} onSaved={load} />

      {/* Personnel, labor, materials, outcome — what the GA is approving margin against */}
      <div className="mb-4 grid gap-3 sm:grid-cols-2" data-testid="billing-work-detail">
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Personnel & Labor</div>
          <div className="text-sm text-slate-700">Primary: <b>{rev.primary_assignee || '—'}</b></div>
          {(rev.participants || []).map((p, i) => (
            <div key={i} className="mt-1 text-xs text-slate-600" data-testid={`billing-participant-${i}`}>
              {p.name} <span className="text-slate-400">({p.classification || p.role || '—'})</span> — {p.labor_hours ?? '—'} hrs
              {p.payable_amount != null && <> · payable ${Number(p.payable_amount).toFixed(2)}</>}
            </div>
          ))}
          <div className="mt-2 text-xs text-slate-500">Outcome: <b className="text-slate-700">{rev.completion_outcome || '—'}</b> · Follow-up: <b className="text-slate-700">{rev.follow_up_required ? 'Yes' : 'No'}</b></div>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3" data-testid="billing-internal-cost">
          <div className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-amber-700"><Lock size={11} /> Internal Job Cost <span className="font-normal normal-case text-amber-600">· not billed to client</span></div>
          <div className="space-y-0.5 text-xs text-slate-600">
            <div className="flex justify-between"><span>Internal labor (payable)</span><span>{money(icb.labor)}</span></div>
            <div className="flex justify-between"><span>Materials cost</span><span>{money(icb.materials_cost)}</span></div>
            {(icb.subcontractor > 0) && <div className="flex justify-between"><span>Subcontractor</span><span>{money(icb.subcontractor)}</span></div>}
            {(icb.travel > 0) && <div className="flex justify-between"><span>Travel</span><span>{money(icb.travel)}</span></div>}
            {(icb.other > 0) && <div className="flex justify-between"><span>Other</span><span>{money(icb.other)}</span></div>}
          </div>
          <div className="mt-2 flex justify-between border-t border-amber-200 pt-1.5 text-xs font-semibold text-amber-800"><span>Total internal cost</span><span data-testid="billing-internal-total">{money(internalCost)}</span></div>
        </div>
      </div>


      {/* Proposed Client Billing — separate from internal cost */}
      <div className="mb-2 flex items-center gap-2 rounded-lg bg-[#0E2A47]/5 px-3 py-2" data-testid="billing-proposed-header">
        <DollarSign size={14} className="text-[#0E2A47]" />
        <span className="text-xs font-bold uppercase tracking-wide text-[#0E2A47]">Proposed Client Billing</span>
      </div>

      {/* Service fee selection */}
      <div className="mb-4 rounded-lg border border-slate-200 p-3" data-testid="billing-service-fee">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Service Fee</div>
          <button onClick={openManageFees} className="text-[11px] font-semibold text-[#0E2A47] hover:underline" data-testid="billing-manage-fees-btn">Manage pre-established fees</button>
        </div>
        {rev.service_fee?.needs_classification && (
          <div className="mb-2 flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] font-semibold text-amber-800" data-testid="fee-needs-classification">
            <ShieldAlert size={13} className="mt-0.5" />
            <span>A ${Number(rev.service_fee.amount).toFixed(2)} fee is recorded but not classified. The Global Administrator must classify it as a named Pre-Established fee or a One-Time fee (with a description) before billing can proceed.</span>
          </div>
        )}
        <div className="space-y-2 text-sm">
          <div className="flex flex-wrap items-center gap-2" data-testid="fee-opt-pre">
            <label className="flex items-center gap-2">
              <input type="radio" name="feeType" checked={feeType === 'pre_established'} onChange={() => setFeeType('pre_established')} />
              <span>Applicable Pre-Established Fee</span>
            </label>
            {feeType === 'pre_established' && (
              <select value={feeTemplateId} onChange={(e) => setFeeTemplateId(e.target.value)} className="min-w-0 flex-1 basis-40 rounded border border-slate-200 px-2 py-1 text-sm" data-testid="fee-template-select">
                <option value="">Select a fee…</option>
                {feeTemplates.map((t) => <option key={t.id} value={t.id}>{t.name} — {money(t.amount)}</option>)}
              </select>
            )}
          </div>
          <label className="flex flex-wrap items-center gap-2" data-testid="fee-opt-one">
            <input type="radio" name="feeType" checked={feeType === 'one_time'} onChange={() => setFeeType('one_time')} />
            <span>One-Time Service Fee</span>
            {feeType === 'one_time' && (
              <span className="ml-1 flex min-w-0 flex-1 basis-40 items-center gap-1">
                <input value={oneTimeName} onChange={(e) => setOneTimeName(e.target.value)} placeholder="Label" className="min-w-0 flex-1 rounded border border-slate-200 px-2 py-1 text-sm" data-testid="fee-onetime-name" />
                <input type="number" value={oneTimeAmount} onChange={(e) => setOneTimeAmount(e.target.value)} placeholder="$" className="w-20 rounded border border-slate-200 px-2 py-1 text-sm" data-testid="fee-onetime-amount" />
              </span>
            )}
          </label>
          <label className="flex items-center gap-2" data-testid="fee-opt-none">
            <input type="radio" name="feeType" checked={feeType === 'none'} onChange={() => setFeeType('none')} />
            <span>No Service Fee</span>
          </label>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs text-slate-500">Selected fee (revenue): <b className="text-[#0E2A47]" data-testid="fee-current-amount">{money(rev.service_fee?.amount)}</b>{rev.service_fee?.name ? ` · ${rev.service_fee.name}` : ''}</span>
          <Button size="sm" variant="outline" onClick={saveServiceFee} disabled={busy} data-testid="billing-save-fee">Save Service Fee</Button>
        </div>
      </div>

      {/* Editable line items (GA) */}
      <div className="mb-4">
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Additional Billing Line Items <span className="font-normal normal-case text-slate-400">(service fee is managed above)</span></div>
        <div className="space-y-2">
          {items.map((li, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`billing-line-${i}`}>
              <input disabled={!isGA} value={li.description || ''} onChange={(e) => { const n = [...items]; n[i] = { ...li, description: e.target.value }; setItems(n); }} placeholder="Description" className="min-w-0 flex-1 rounded border border-slate-200 px-2 py-1 text-sm disabled:bg-slate-50" />
              <input disabled={!isGA} type="number" value={li.quantity ?? ''} onChange={(e) => { const n = [...items]; n[i] = { ...li, quantity: e.target.value, amount: (Number(e.target.value||0)*Number(li.unit_price||0)) }; setItems(n); }} placeholder="Qty" className="w-16 rounded border border-slate-200 px-2 py-1 text-sm disabled:bg-slate-50" />
              <input disabled={!isGA} type="number" value={li.unit_price ?? ''} onChange={(e) => { const n = [...items]; n[i] = { ...li, unit_price: e.target.value, amount: (Number(li.quantity||0)*Number(e.target.value||0)) }; setItems(n); }} placeholder="Unit $" className="w-20 rounded border border-slate-200 px-2 py-1 text-sm disabled:bg-slate-50" />
              <span className="w-24 text-right text-sm font-semibold">{money(li.amount || (Number(li.quantity||0)*Number(li.unit_price||0)))}</span>
              {isGA && <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>}
            </div>
          ))}
          {isGA && <button onClick={() => setItems([...items, { description: '', quantity: 1, unit_price: 0, amount: 0 }])} className="inline-flex items-center gap-1 text-xs font-semibold text-[#0E2A47]" data-testid="billing-add-line"><Plus size={13} /> Add line item</button>}
        </div>
        <div className="mt-2 text-[11px] text-slate-400" data-testid="billing-total-composition">Proposed Billing = Service Fee {money(serviceFee)} + Additional Line Items {money(lineTotal)}</div>
        <div className="mt-2 grid grid-cols-4 gap-2 rounded-lg bg-slate-50 px-3 py-2 text-center text-xs">
          <div><div className="text-slate-400">Proposed Billing</div><div className="font-bold text-[#0E2A47]" data-testid="billing-total">{money(total)}</div></div>
          <div><div className="text-slate-400">Internal Cost</div><div className="font-bold">{money(internalCost)}</div></div>
          <div><div className="text-slate-400">Gross Profit</div><div className="font-bold text-emerald-600">{marginCalculable ? money(gp) : '—'}</div></div>
          <div><div className="text-slate-400">Margin</div><div className="font-bold text-emerald-600" data-testid="billing-margin">{marginCalculable ? `${margin}%` : <span className="text-[10px] font-semibold text-slate-400">Margin not calculated</span>}</div></div>
        </div>
        {isGA && <Button size="sm" variant="outline" className="mt-2" onClick={saveItems} disabled={busy} data-testid="billing-save-lines">Save Line Items</Button>}
      </div>

      {/* Workflow actions */}
      <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
        {billingPhase && <Button size="sm" variant="outline" onClick={loadPreview} data-testid="billing-preview-btn"><FileText size={14} className="mr-1" /> Preview Package</Button>}
        {billingPhase && ['Not Submitted', 'Work Performed - Closeout Incomplete', 'Completion Submitted', 'Returned', 'Reopened'].includes(st) && <Button size="sm" onClick={() => act('send-to-ops-review', {}, 'Sent to Operations Review')} disabled={busy || !!rev.data_exception || !ready} title={!ready ? 'Complete closeout requirements first' : ''} data-testid="billing-send-ops-review-btn">Send to Operations Review</Button>}
        {billingPhase && st === 'Pending Operations Review' && <Button size="sm" onClick={() => act('submit-for-review', {}, 'Submitted for approval')} disabled={busy || !!rev.data_exception || !ready} title={!ready ? 'Complete closeout requirements first' : ''} data-testid="billing-submit-btn">Submit for Billing Review</Button>}
        {isGA && st === 'Ready for Global Administrator Approval' && <Button size="sm" onClick={() => act('approve', {}, 'Approved for billing')} disabled={busy || !ready} title={!ready ? 'Complete closeout requirements first' : ''} data-testid="billing-approve-btn"><CheckCircle2 size={14} className="mr-1" /> Approve for Billing</Button>}
        {isGA && st === 'Approved for Billing - Delivery Pending' && <Button size="sm" onClick={() => act('send-to-accounting', {}, 'Accounting package prepared')} disabled={busy} data-testid="billing-send-btn"><Send size={14} className="mr-1" /> Approve &amp; Send to Accounting</Button>}
        {isGA && st === 'Sent to Accounting' && <Button size="sm" variant="outline" onClick={() => act('invoiced', {}, 'Marked Invoiced/Closed')} disabled={busy} data-testid="billing-invoiced-btn">Mark Invoiced/Closed</Button>}
        <Button size="sm" variant="ghost" onClick={doReturn} disabled={busy} data-testid="billing-return-btn">Return Incomplete</Button>
        {rev.operational_locked && <Button size="sm" variant="ghost" onClick={doReopen} disabled={busy} data-testid="billing-reopen-btn"><RotateCcw size={14} className="mr-1" /> Reopen</Button>}
      </div>

      {/* Documents — preview & download (TEST copies) — only once work reaches billing review */}
      {billingPhase && (
      <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="billing-documents">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Documents</div>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="flex items-center justify-between rounded-md bg-white px-3 py-2 text-sm ring-1 ring-slate-200">
            <span className="font-medium text-[#0E2A47]">Internal Billing Package PDF</span>
            <span className="flex gap-1">
              <button onClick={() => openPdf('internal-package.pdf', `Internal_Billing_${rev.identifiers?.rout_work_order_number || 'WO'}.pdf`, false)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title="Preview" data-testid="pdf-internal-preview"><Eye size={15} /></button>
              <button onClick={() => openPdf('internal-package.pdf', `Internal_Billing_${rev.identifiers?.rout_work_order_number || 'WO'}.pdf`, true)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title="Download" data-testid="pdf-internal-download"><Download size={15} /></button>
            </span>
          </div>
          <div className="flex items-center justify-between rounded-md bg-white px-3 py-2 text-sm ring-1 ring-slate-200">
            <span className="font-medium text-[#0E2A47]">Client Completion Report PDF</span>
            <span className="flex gap-1">
              <button onClick={() => openPdf('client-completion.pdf', `Completion_Summary_${rev.identifiers?.rout_work_order_number || 'WO'}.pdf`, false)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title="Preview" data-testid="pdf-client-preview"><Eye size={15} /></button>
              <button onClick={() => openPdf('client-completion.pdf', `Completion_Summary_${rev.identifiers?.rout_work_order_number || 'WO'}.pdf`, true)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title="Download" data-testid="pdf-client-download"><Download size={15} /></button>
            </span>
          </div>
        </div>
        <p className="mt-2 text-[11px] text-slate-400">Use "Preview Package" above for the four email previews (internal billing, billing confirmation, labor/payroll, client completion).</p>
      </div>
      )}

      {/* Audit trail */}
      {rev.billing_audit?.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3" data-testid="billing-audit">
          <div className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500"><History size={12} /> Billing Audit</div>
          <ul className="space-y-1 text-xs text-slate-500">
            {rev.billing_audit.slice(-6).reverse().map((a, i) => (
              <li key={a.id || i}><span className="font-semibold text-slate-600">{a.action}</span> — {a.detail} · {a.by_name} · {_fmtDateTime(a.at)}</li>
            ))}
          </ul>
        </div>
      )}

      {showPreview && preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowPreview(false)} data-testid="billing-preview-modal">
          <div className="max-h-[80vh] w-full max-w-2xl overflow-auto rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between"><h4 className="font-bold text-[#0E2A47]">Prepared Documents — Preview (no email sent)</h4><button onClick={() => setShowPreview(false)}>✕</button></div>
            <p className="mb-3 text-xs text-amber-600">{preview.note}</p>
            {['internal_billing_package', 'billing_confirmation', 'labor_payroll_notice', 'client_completion_email'].map((k) => (
              <div key={k} className="mb-3 rounded-lg border border-slate-200 p-3 text-sm">
                <div className="font-semibold text-[#0E2A47]">{k.replace(/_/g, ' ')}</div>
                <div className="text-xs text-slate-500">To: {(preview[k].recipients || []).join(', ') || '—'}</div>
                <div className="text-xs text-slate-500">Subject: {preview[k].subject}</div>
                {preview[k].body && <div className="mt-1 text-xs text-slate-600">{preview[k].body}</div>}
                {preview[k].attachments && <div className="mt-1 text-[11px] text-slate-400">Attachments: {preview[k].attachments.join(', ')}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
      {manageFees && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setManageFees(false)} data-testid="manage-fees-modal">
          <div className="max-h-[80vh] w-full max-w-lg overflow-auto rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h4 className="font-bold text-[#0E2A47]">Pre-Established Service Fees — {rev.customer?.name}</h4>
              <button onClick={() => setManageFees(false)}>✕</button>
            </div>
            <p className="mb-3 text-xs text-slate-500">These templates let Operations apply a standard fee quickly on any Work Order for this customer.</p>
            <div className="space-y-2">
              {feeDraft.map((t, i) => (
                <div key={t.id || i} className="flex items-center gap-2" data-testid={`fee-draft-${i}`}>
                  <input value={t.name || ''} onChange={(e) => { const n = [...feeDraft]; n[i] = { ...t, name: e.target.value }; setFeeDraft(n); }} placeholder="Fee name" className="flex-1 rounded border border-slate-200 px-2 py-1 text-sm" />
                  <input type="number" value={t.amount ?? ''} onChange={(e) => { const n = [...feeDraft]; n[i] = { ...t, amount: e.target.value }; setFeeDraft(n); }} placeholder="$" className="w-24 rounded border border-slate-200 px-2 py-1 text-sm" />
                  <button onClick={() => setFeeDraft(feeDraft.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                </div>
              ))}
              <button onClick={() => setFeeDraft([...feeDraft, { name: '', amount: '', active: true }])} className="inline-flex items-center gap-1 text-xs font-semibold text-[#0E2A47]" data-testid="fee-add-template"><Plus size={13} /> Add fee template</button>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setManageFees(false)}>Cancel</Button>
              <Button size="sm" onClick={saveFeeTemplates} disabled={busy} data-testid="fee-templates-save">Save Templates</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingPanel;
